import { useCallback } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';

import { buildSchema } from './schema';

type UseIssuePromptReturn = ModelHookResult<
	{
		schema: ReturnType<typeof buildSchema>;
		defaultValues: { issue: string };
	},
	{
		submit: (data: { issue: string }) => void;
		back: () => void;
		persistDraft: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
	}
>;

export function useIssuePrompt(): UseIssuePromptReturn {
	const { actions, data } = useStartAPetitionPageState();
	const track = useTracking();
	const { translate } = useI18n();
	const { draft } = data;

	const submit = useCallback(
		(values: { issue: string }) => {
			void track('sap_issue_submit', { prompt: values.issue });

			actions.send({
				type: 'submit',
				draft: { input: { issuePrompt: values.issue } },
			});
		},
		[actions, track],
	);

	const back = useCallback(() => {
		void track('sap_issue_back');
		actions.send({ type: 'back', target: null });
	}, [actions, track]);

	const persistDraft = useCallback(
		(event: React.FocusEvent<HTMLTextAreaElement>) => {
			actions.send({
				type: 'submit',
				draft: { input: { issuePrompt: event.target.value.trim() } },
				target: 'self',
			});
		},
		[actions],
	);

	return {
		data: {
			schema: buildSchema({ translate }),
			defaultValues: { issue: draft?.input?.issuePrompt ?? '' },
		},
		actions: { submit, back, persistDraft },
	};
}
